'use client';

interface PrivacyItem {
  title: string;
  content: string | string[];
}

interface PrivacyPolicySectionProps {
  title: string;
  items: PrivacyItem[];
}

export default function PrivacyPolicySection({ title, items }: PrivacyPolicySectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 space-y-4">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold text-foreground">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            Your privacy and data security are our top priorities. This policy explains how we collect, use, and protect your personal information in compliance with applicable data protection regulations.
          </p>
        </div>
        
        <div className="space-y-8">
          {items.map((item, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-8 shadow-sky-sm hover:shadow-sky-md transition-all duration-300 border border-border/30"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-gradient-to-br from-primary via-accent to-primary">
                    <span className="text-sm font-headline font-bold text-primary-foreground">
                      {index + 1}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-headline font-bold text-foreground mb-4">
                    {item.title}
                  </h3>
                  {Array.isArray(item.content) ? (
                    <ul className="space-y-3">
                      {item.content.map((point, idx) => (
                        <li key={idx} className="flex gap-3 text-muted-foreground font-body leading-relaxed">
                          <span className="text-primary font-bold mt-1">✓</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground font-body leading-relaxed">
                      {item.content}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/20 rounded-xl p-8">
          <h4 className="font-headline font-bold text-foreground mb-4">Your Rights Under Data Protection Laws</h4>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <p className="font-headline font-semibold text-foreground">Right to Access</p>
              <p className="text-sm text-muted-foreground font-body">
                Request and access your personal data at any time.
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-headline font-semibold text-foreground">Right to Correct</p>
              <p className="text-sm text-muted-foreground font-body">
                Update or correct inaccurate personal information.
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-headline font-semibold text-foreground">Right to Erasure</p>
              <p className="text-sm text-muted-foreground font-body">
                Request deletion of your data after course completion.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
